// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import {
  Button,
  Checkbox,
  Container,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
  Table,
  TextArea,
} from 'semantic-ui-react';
import {
  brokerageEventsApi,
  type BrokerageBackfillReport,
} from '../api/brokerageEventsApi';
import { parseBrokerageIds } from './brokerageBackfillUtils';

function BrokerageBackfillPage() {
  const [dryRun, setDryRun] = useState(true);
  const [brokerageIdsRaw, setBrokerageIdsRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<BrokerageBackfillReport | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { ids: brokerageIds, invalid: invalidIds } = parseBrokerageIds(brokerageIdsRaw);
  const hasInvalid = invalidIds.length > 0;

  const runBackfill = async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const result = await brokerageEventsApi.runBackfill({
        dryRun,
        brokerageIds: brokerageIds.length > 0 ? brokerageIds : undefined,
      });
      setReport(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Backfill failed');
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const handleClick = () => {
    if (hasInvalid) return;
    if (dryRun) {
      void runBackfill();
    } else {
      setConfirmOpen(true);
    }
  };

  return (
    <Container>
      <Header as="h2">
        <Icon name="paper plane" />
        <Header.Content>
          Brokerage Event Backfill
          <Header.Subheader>
            Replay BROKERAGE_CREATED events for existing brokerages onto the brokerage.events topic
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <p>
          This tool publishes a <code>BROKERAGE_CREATED</code> event with{' '}
          <code>source=BACKFILL</code> for every brokerage (or the subset listed below). Use this
          after wiring up a new downstream consumer that needs to bootstrap from the current
          brokerage roster. Each run emits new events with fresh <code>eventId</code>s — consumers
          must be idempotent on <code>brokerage.id</code>.
        </p>

        <Segment basic>
          <Checkbox
            toggle
            label="Dry run (count only, do not publish)"
            checked={dryRun}
            onChange={(_, data) => setDryRun(Boolean(data.checked))}
            disabled={loading}
          />
        </Segment>

        <Segment basic>
          <label htmlFor="brokerageIds" style={{ display: 'block', marginBottom: 4 }}>
            Restrict to specific brokerage IDs (optional, comma- or newline-separated UUIDs)
          </label>
          <TextArea
            id="brokerageIds"
            value={brokerageIdsRaw}
            onChange={(_, data) => setBrokerageIdsRaw(String(data.value ?? ''))}
            placeholder="leave empty to backfill all brokerages"
            rows={4}
            style={{ width: '100%', fontFamily: 'monospace' }}
            disabled={loading}
          />
          {hasInvalid && (
            <Message warning size="small">
              <Message.Header>Invalid UUIDs ignored</Message.Header>
              <Message.List>
                {invalidIds.map((bad, idx) => (
                  <Message.Item key={idx}>{bad}</Message.Item>
                ))}
              </Message.List>
            </Message>
          )}
        </Segment>

        <Button
          primary
          loading={loading}
          disabled={loading || hasInvalid}
          onClick={handleClick}
        >
          <Icon name="sync" />
          {dryRun ? 'Run dry-run' : 'Run backfill'}
        </Button>
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Backfill error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {report && (
        <Segment>
          <Header as="h3">Backfill report</Header>
          <Table definition compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Dry run</Table.Cell>
                <Table.Cell>{report.dryRun ? 'Yes' : 'No'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Total scanned</Table.Cell>
                <Table.Cell>{report.totalScanned}</Table.Cell>
              </Table.Row>
              <Table.Row positive={report.eventsEmitted > 0}>
                <Table.Cell>Events emitted</Table.Cell>
                <Table.Cell>{report.eventsEmitted}</Table.Cell>
              </Table.Row>
              <Table.Row negative={report.skipped > 0}>
                <Table.Cell>Skipped</Table.Cell>
                <Table.Cell>{report.skipped}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Started at</Table.Cell>
                <Table.Cell>{report.startedAt}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Completed at</Table.Cell>
                <Table.Cell>{report.completedAt}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          {report.errors.length > 0 && (
            <Message warning>
              <Message.Header>Per-row errors</Message.Header>
              <Message.List>
                {report.errors.map((err, idx) => (
                  <Message.Item key={idx}>
                    <code>{err.brokerageId}</code>: {err.message}
                  </Message.Item>
                ))}
              </Message.List>
            </Message>
          )}

          {report.errors.length === 0 && !report.dryRun && report.eventsEmitted > 0 && (
            <Message positive>
              <Message.Header>Backfill complete</Message.Header>
              <p>
                {report.eventsEmitted} BROKERAGE_CREATED event
                {report.eventsEmitted === 1 ? '' : 's'} published with source=BACKFILL.
              </p>
            </Message>
          )}
        </Segment>
      )}

      <Modal
        open={confirmOpen}
        size="small"
        onClose={() => !loading && setConfirmOpen(false)}
      >
        <Modal.Header>Publish backfill events?</Modal.Header>
        <Modal.Content>
          <p>
            This will publish{' '}
            <strong>
              {brokerageIds.length > 0 ? brokerageIds.length : 'all'}
            </strong>{' '}
            BROKERAGE_CREATED event{brokerageIds.length === 1 ? '' : 's'} on the{' '}
            <code>brokerage.events</code> topic with <code>source=BACKFILL</code>. Downstream
            consumers will receive them. This action cannot be undone.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button primary loading={loading} disabled={loading} onClick={runBackfill}>
            <Icon name="paper plane" />
            Publish events
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default BrokerageBackfillPage;
